import {
  inject,
  Injectable,
  signal,
  computed,
  WritableSignal,
} from '@angular/core';
import { catchError, finalize, of } from 'rxjs';
import { NotificationService } from './notification.service';
import {
  NotificationPaginationState,
  SecurepayNotification,
} from './notification.model';

const initialPagination: NotificationPaginationState = {
  page: 0,
  pageSize: 10,
  totalPages: 0,
  recordsTotal: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

@Injectable({ providedIn: 'root' })
export class NotificationStore {
  private readonly notificationService = inject(NotificationService);

  private readonly notificationsSignal: WritableSignal<
    SecurepayNotification[]
  > = signal([]);
  private readonly loadingSignal: WritableSignal<boolean> = signal(false);
  private readonly loadingMoreSignal: WritableSignal<boolean> = signal(false);
  private readonly errorSignal: WritableSignal<string | null> = signal(null);
  private readonly loadMoreErrorSignal: WritableSignal<string | null> =
    signal(null);
  private readonly paginationSignal: WritableSignal<NotificationPaginationState> =
    signal({ ...initialPagination });

  readonly notifications = this.notificationsSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly loadingMore = this.loadingMoreSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly loadMoreError = this.loadMoreErrorSignal.asReadonly();
  readonly pagination = this.paginationSignal.asReadonly();

  readonly hasNotifications = computed(
    () => this.notificationsSignal().length > 0,
  );

  /** More pages exist when the list has not reached the server total count. */
  readonly canShowMore = computed(() => {
    const listLen = this.notificationsSignal().length;
    const total = this.paginationSignal().recordsTotal;
    return total > 0 && listLen < total;
  });

  /**
   * Loads a page of notifications. Replaces the list by default; use loadMore for append.
   */
  loadNotifications(
    page: number = this.paginationSignal().page,
    pageSize: number = this.paginationSignal().pageSize,
    options: { replace?: boolean } = {},
  ): void {
    const replace = options.replace !== false;
    if (replace) {
      this.loadingSignal.set(true);
      this.errorSignal.set(null);
      this.loadMoreErrorSignal.set(null);
    } else {
      this.loadingMoreSignal.set(true);
      this.loadMoreErrorSignal.set(null);
    }

    this.notificationService
      .getNotifications(page, pageSize)
      .pipe(
        finalize(() => {
          if (replace) {
            this.loadingSignal.set(false);
          } else {
            this.loadingMoreSignal.set(false);
          }
        }),
        catchError((err: { message?: string }) => {
          const msg = err.message ?? 'Failed to load notifications.';
          if (replace) {
            this.errorSignal.set(msg);
            this.notificationsSignal.set([]);
          } else {
            this.loadMoreErrorSignal.set(msg);
          }
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }
        if (response.success && response.data) {
          const incoming = response.data.data;
          if (replace) {
            this.notificationsSignal.set(incoming);
          } else {
            const existingIds = new Set(
              this.notificationsSignal().map((n) => n.id),
            );
            const merged = incoming.filter((n) => !existingIds.has(n.id));
            this.notificationsSignal.update((prev) => [...prev, ...merged]);
          }
          this.paginationSignal.set({
            page: response.data.pageIndex,
            pageSize: response.data.pageSize,
            totalPages: response.data.totalPages,
            recordsTotal: response.data.recordsTotal,
            hasNextPage: response.data.hasNextPage,
            hasPreviousPage: response.data.hasPreviousPage,
          });
        } else if (replace) {
          this.notificationsSignal.set([]);
          this.errorSignal.set(
            response.message ?? 'Unable to load notifications.',
          );
        } else {
          this.loadMoreErrorSignal.set(
            response.message ?? 'Unable to load more notifications.',
          );
        }
      });
  }

  /** Fetches the next page (same page size) and appends to the current list. */
  loadMoreNotifications(): void {
    if (!this.canShowMore()) {
      return;
    }
    if (this.loadingSignal() || this.loadingMoreSignal()) {
      return;
    }
    const { page, pageSize } = this.paginationSignal();
    const nextPage = page + 1;
    this.loadNotifications(nextPage, pageSize, { replace: false });
  }

  resetPagination(): void {
    this.paginationSignal.set({ ...initialPagination });
  }
}
